/**
 * localStorage keys that hold per-session auth state, and the cleanup run
 * when a session EXPIRES (server-rejected refresh token, revoked session).
 *
 * Kept free of app imports so it is unit-testable — see
 * src/__tests__/auth/sessionStorage.test.ts.
 *
 * A deliberate "Sign out" does NOT call this: it keeps the cached profile so a
 * staff member can still sign in offline during a shift hand-off. Expiry is
 * different — it is only ever declared when the auth server confirmed it (see
 * sessionHealth.ts), so the user can sign in online straight away and the
 * caches repopulate. Leaving the expired user's profile cached would let
 * authService.getCurrentUser() hand that user straight back with no session.
 */

export const USER_PROFILE_CACHE_KEY = 'smartpos_user_profiles';
export const LAST_USER_ID_KEY = 'smartpos_last_user_id';
export const ACCESS_CONTROL_CACHE_KEY = 'smartpos_access_control_codes';

// Only the expired user's entries are removed from the per-user caches, so
// other staff on a shared terminal keep offline sign-in. Device keys are left
// alone on purpose — notably 'outlet', which picks the web-fallback database
// file (clearing it would orphan that outlet's queued sales), plus the
// sign-in method preference, KDS settings and feature-release flags.

// Supabase stores its session (`sb-<ref>-auth-token`) and PKCE verifier
// (`sb-<ref>-auth-token-code-verifier`) under this prefix.
const SUPABASE_KEY_PREFIX = 'sb-';

function safeRemove(storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    /* ignore — storage unavailable */
  }
}

function removeUserEntry(
  storage: Storage,
  cacheKey: string,
  userId: string | null,
): void {
  if (!userId) return;
  try {
    const raw = storage.getItem(cacheKey);
    if (!raw) return;
    const map = JSON.parse(raw) as Record<string, unknown>;
    delete map[userId];
    storage.setItem(cacheKey, JSON.stringify(map));
  } catch {
    /* ignore — unreadable cache; nothing to clean */
  }
}

/**
 * Removes the expired session's auth state from `storage`. `userId` is the
 * signed-in user at the moment of expiry; falls back to the last cached user.
 */
export function clearExpiredSessionStorage(
  storage: Storage,
  userId?: string | null,
): void {
  let lastUserId: string | null = null;
  try {
    lastUserId = storage.getItem(LAST_USER_ID_KEY);
  } catch {
    /* ignore */
  }
  const expiredUserId = userId || lastUserId;
  removeUserEntry(storage, USER_PROFILE_CACHE_KEY, expiredUserId);
  removeUserEntry(storage, ACCESS_CONTROL_CACHE_KEY, expiredUserId);
  // LAST_USER_ID_KEY drives getCurrentUser()'s no-session fallback — always
  // drop it, or it could hand back a profile (this user's or another staff
  // member's) with no session behind it. Offline sign-in still matches cached
  // profiles by email (loginUser), so nothing else depends on it.
  safeRemove(storage, LAST_USER_ID_KEY);

  let supabaseKeys: string[] = [];
  try {
    supabaseKeys = Array.from({ length: storage.length }, (_, i) =>
      storage.key(i),
    ).filter((key): key is string => !!key?.startsWith(SUPABASE_KEY_PREFIX));
  } catch {
    /* ignore */
  }
  supabaseKeys.forEach((key) => safeRemove(storage, key));
}
